import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Button, CircularProgress, Alert, Divider,
  Dialog, DialogTitle, DialogContent, DialogActions,
} from '@mui/material';
import {
  findTenantsDueForPurge,
  runScheduledPurge,
  getPurgeLogs,
  purgeTenant,
} from '../components/services/purgeService';
import { useLegalAuth as useAuth } from '../hooks/useLegalAuth';

export default function TenantPurgeAdmin() {
  const { currentUser } = useAuth();
  const [dueTenants, setDueTenants] = useState(null);
  const [logs, setLogs] = useState(null);
  const [running, setRunning] = useState(false);
  const [purgingTenantId, setPurgingTenantId] = useState(null);
  const [lastResult, setLastResult] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);

  const loadDueTenants = async () => {
    const due = await findTenantsDueForPurge();
    setDueTenants(due);
  };

  const loadLogs = async () => {
    const l = await getPurgeLogs();
    setLogs(l.sort((a, b) => (b.purgedAt?.toMillis?.() || 0) - (a.purgedAt?.toMillis?.() || 0)));
  };

  useEffect(() => { loadDueTenants(); loadLogs(); }, []);

  const handleRunPurgeClick = () => {
    setConfirmOpen(true);
  };

  const handleConfirmPurge = async () => {
    setConfirmOpen(false);
    setRunning(true);
    try {
      const results = await runScheduledPurge(currentUser?.email);
      setLastResult(results);
      await loadDueTenants();
      await loadLogs();
    } catch (err) {
      alert('Purge run failed: ' + err.message);
    } finally {
      setRunning(false);
    }
  };

  const handleSingleTenantPurge = async (tenantId) => {
    if (!tenantId) return;
    setPurgingTenantId(tenantId);
    setRunning(true);
    try {
      const result = await purgeTenant(tenantId, currentUser?.email || 'unknown');
      setLastResult([result]);
      await loadDueTenants();
      await loadLogs();
    } catch (err) {
      alert(`Purge failed for tenant ${tenantId}: ${err.message}`);
    } finally {
      setPurgingTenantId(null);
      setRunning(false);
    }
  };

  const cardBase = {
    background: '#ffffff',
    border: '1px solid #E8DDF7',
    borderRadius: 2,
    boxShadow: '0 8px 24px rgba(124, 58, 237, 0.08)',
  };

  const purgeButtonStyles = {
    textTransform: 'none',
    fontWeight: 700,
    minHeight: 36,
    height: 36,
    px: 1.8,
    fontSize: 13,
    borderRadius: 1.5,
    background: 'linear-gradient(135deg, #d11a2a 0%, #b3122b 100%)',
    boxShadow: '0 6px 14px rgba(209, 26, 42, 0.22)',
    '&:hover': {
      background: 'linear-gradient(135deg, #c81226 0%, #a30f24 100%)',
    },
    '&:disabled': {
      background: '#d6d3d1',
      color: '#fff',
      boxShadow: 'none',
    },
  };

  return (
    <Box sx={{ maxWidth: 980, mx: 'auto', p: { xs: 2, md: 4 } }}>
      <Typography variant="h4" sx={{ fontWeight: 800, mb: 2, color: '#1F2937' }}>Tenant Purge</Typography>

      <Typography sx={{ mb: 3, color: '#5B5A6A', fontSize: 18, lineHeight: 1.6 }}>
        This does not run automatically. Check periodically and click below to permanently delete
        data for tenants whose 30-day deletion window has passed.
      </Typography>

      {dueTenants === null ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress size={26} sx={{ color: '#7C3AED' }} />
        </Box>
      ) : (
        <Alert
          severity={dueTenants.length > 0 ? 'warning' : 'success'}
          sx={{
            mb: 4,
            borderRadius: 2,
            fontSize: 15,
            px: 2,
            py: 1,
            '& .MuiAlert-message': { fontWeight: 600 },
          }}
        >
          {dueTenants.length} tenant(s) currently due for permanent deletion.
        </Alert>
      )}

      <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)}>
        <DialogTitle sx={{ fontWeight: 800 }}>Are you sure?</DialogTitle>
        <DialogContent>
          <Typography sx={{ color: '#4B5563' }}>
            This will permanently delete all data for {dueTenants?.length || 0} tenant
            {dueTenants?.length === 1 ? '' : 's'} across every configured collection.
            This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setConfirmOpen(false)} sx={{ textTransform: 'none', fontWeight: 600 }}>
            Cancel
          </Button>
          <Button color="error" variant="contained" onClick={handleConfirmPurge} sx={{ textTransform: 'none', fontWeight: 700 }}>
            Yes, proceed
          </Button>
        </DialogActions>
      </Dialog>

      {dueTenants && dueTenants.length > 0 && (
        <Box sx={{ mb: 5 }}>
          <Typography variant="h6" sx={{ fontWeight: 800, mb: 2, color: '#1F2937' }}>Due tenants</Typography>
          <Box sx={{ ...cardBase, p: 2 }}>
            {dueTenants.map((tenant) => (
              <Box
                key={tenant.id}
                sx={{
                  mb: 2,
                  p: 2,
                  border: '1px solid #F0E7FF',
                  borderRadius: 2,
                  background: '#FAF7FF',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: 2,
                  flexWrap: 'wrap',
                }}
              >
                <Box>
                  <Typography sx={{ fontWeight: 700, color: '#1F2937' }}>
                    {tenant.tenantName || 'Unnamed tenant'}
                  </Typography>
                  <Typography sx={{ fontSize: 12, color: '#6B7280', mt: 0.5 }}>
                    ID: {tenant.id}
                  </Typography>
                </Box>

                <Button
                  variant="contained"
                  onClick={() => handleSingleTenantPurge(tenant.id)}
                  disabled={running || purgingTenantId === tenant.id}
                  sx={{ ...purgeButtonStyles, minWidth: 170, whiteSpace: 'nowrap' }}
                >
                  {purgingTenantId === tenant.id ? 'Purging...' : 'Run Tenant Purge Now'}
                </Button>
              </Box>
            ))}

            <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
              <Button
                variant="contained"
                onClick={handleRunPurgeClick}
                disabled={running || !dueTenants || dueTenants.length === 0}
                sx={{ ...purgeButtonStyles, minWidth: 190 }}
              >
                {running ? 'Running...' : 'Purge All Due Tenants'}
              </Button>
            </Box>
          </Box>
        </Box>
      )}

      {lastResult && (
        <Box sx={{ mb: 4, p: 2.5, background: '#F5F0FF', borderRadius: 2, border: '1px solid #E8DDF7' }}>
          <Typography sx={{ fontWeight: 800, mb: 1, color: '#4C1D95' }}>Last run result:</Typography>
          {lastResult.map((r, i) => (
            <Typography key={i} sx={{ fontSize: 13, color: '#374151' }}>
              {r.tenantId} — {r.status}
            </Typography>
          ))}
        </Box>
      )}

      <Divider sx={{ my: 3, borderColor: '#E5E7EB' }} />

      <Typography variant="h6" sx={{ fontWeight: 800, mb: 2, color: '#1F2937' }}>Purge History</Typography>
      {logs === null ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress size={22} sx={{ color: '#7C3AED' }} />
        </Box>
      ) : logs.length === 0 ? (
        <Typography sx={{ color: '#6B7280', fontSize: 16 }}>No purges have been run yet.</Typography>
      ) : (
        logs.map((log) => (
          <Box key={log.id} sx={{ ...cardBase, mb: 2, p: 2 }}>
            <Typography sx={{ fontWeight: 700, color: '#1F2937' }}>
              Tenant: {log.tenantId} — {log.status}
            </Typography>
            <Typography sx={{ fontSize: 12, color: '#6B7280', mt: 0.5 }}>
              Purged {log.purgedAt?.toDate ? log.purgedAt.toDate().toLocaleString() : ''} by {log.triggeredBy}
            </Typography>
            {log.collectionsProcessed?.map((c, i) => (
              <Typography key={i} sx={{ fontSize: 12, color: '#374151', mt: 0.5 }}>
                {c.collection}: {c.docsDeleted} deleted{c.error ? ` (error: ${c.error})` : ''}
              </Typography>
            ))}
          </Box>
        ))
      )}
    </Box>
  );
}